/**
 * Generate a unique session ID
 */
export function generateSessionId() {
    return `tp-ai-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
/**
 * Generate a unique message ID
 */
export function generateMessageId() {
    return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
/**
 * Create a new empty conversation
 */
export function createConversation(sessionId) {
    const now = Date.now();
    return {
        id: sessionId || generateSessionId(),
        messages: [],
        dataSourceId: undefined,
        createdAt: now,
        updatedAt: now,
    };
}
/**
 * Create a user message
 */
export function createUserMessage(content) {
    return {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: Date.now(),
    };
}
/**
 * Create an assistant message
 */
export function createAssistantMessage(content, metadata) {
    return {
        id: generateMessageId(),
        role: 'assistant',
        content,
        timestamp: Date.now(),
        metadata,
    };
}
/**
 * Create a system message
 */
export function createSystemMessage(content) {
    return {
        id: generateMessageId(),
        role: 'system',
        content,
        timestamp: Date.now(),
    };
}
/**
 * Add a message to a conversation (immutably)
 */
export function addMessageToConversation(conversation, message) {
    return {
        ...conversation,
        messages: [...conversation.messages, message],
        updatedAt: Date.now(),
    };
}
/**
 * Update the data source for a conversation
 */
export function setConversationDataSource(conversation, dataSourceId) {
    return {
        ...conversation,
        dataSourceId,
        updatedAt: Date.now(),
    };
}
/**
 * Serialize a conversation to JSON string for storage
 */
export function serializeConversation(conversation) {
    return JSON.stringify(conversation);
}
/**
 * Deserialize a conversation from JSON string
 */
export function deserializeConversation(json) {
    try {
        const parsed = JSON.parse(json);
        // Basic validation
        if (typeof parsed !== 'object'
            || !parsed.id
            || !Array.isArray(parsed.messages)) {
            return null;
        }
        return parsed;
    }
    catch {
        return null;
    }
}
/**
 * Get the messages formatted for the AI API
 * Excludes system messages and metadata
 */
export function getMessagesForAPI(conversation) {
    return conversation.messages
        .filter(m => m.role !== 'system')
        .map(m => ({
        role: m.role,
        content: m.content,
    }));
}
/**
 * Get the most recent query result data stored in the conversation, if any
 */
export function getLatestConversationData(conversation) {
    for (let i = conversation.messages.length - 1; i >= 0; i--) {
        const data = conversation.messages[i].metadata?.data;
        if (data && data.length > 0) {
            return data;
        }
    }
    return null;
}
/**
 * Trim conversation to last N messages to manage context window
 */
export function trimConversation(conversation, maxMessages) {
    if (conversation.messages.length <= maxMessages) {
        return conversation;
    }
    return {
        ...conversation,
        messages: conversation.messages.slice(-maxMessages),
        updatedAt: Date.now(),
    };
}
/**
 * Get conversation statistics
 */
export function getConversationStats(conversation) {
    const messages = conversation.messages;
    const queriesExecuted = messages.filter(m => m.metadata?.query).length;
    const failedQueries = messages.filter(m => m.metadata?.error).length;
    return {
        messageCount: messages.length,
        userMessageCount: messages.filter(m => m.role === 'user').length,
        assistantMessageCount: messages.filter(m => m.role === 'assistant').length,
        queriesExecuted,
        successfulQueries: queriesExecuted - failedQueries,
        failedQueries,
    };
}
//# sourceMappingURL=session.js.map